import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { convertGoogleLatLng } from '@core/utils/location/addressHandler.utils';
import { PolygonF as PolygonGoogleMaps } from '@react-google-maps/api';
import { MAX_EDITABLE_VERTICES } from '~/IsochronousMap/IsochronousMap.utils';
import { useFielderSnackBar } from '~/FielderElements/useFielderSnackBar';

const Polygon = ({ value, onClick, globalVisible, globalEditable, defaultShow, hasCoverageBoundary }) => {
	const { id, info, options } = value;
	const polygonRef = useRef();
	const listenersRef = useRef([]);
	const fielderMsg = useFielderSnackBar();
	const clickHandler = options.onClick ? options.onClick : onClick;
	const pathLength = options.path ? options.path.length : 0;
	const exceedsLimit = pathLength > MAX_EDITABLE_VERTICES;
	//const isEditableBase = options.editable ? options.editable : globalEditable.has(id);
	const isEditableBase = globalEditable.has(id) && (options.editable !== false);
	const isEditable = !exceedsLimit && isEditableBase;
	const isVisibleBase = options.visible ? options.visible : globalVisible.has(id) || defaultShow;
	const isVisible = !hasCoverageBoundary && isVisibleBase;

	const handleClick = () => {
		/*if (exceedsLimit) fielderMsg({
			message: `Editing disabled: polygon has ${pathLength} vertices (max ${MAX_EDITABLE_VERTICES}).`,
			variant: 'warning',
		});
		else*/
		clickHandler(info, options);
	}
	const onEdit = useCallback(() => {
		if (exceedsLimit) return;
		if (options.onChangeVertices && polygonRef.current) {
			const path = polygonRef.current.getPath().getArray().map(convertGoogleLatLng);
			options.onChangeVertices(path);
		}
	}, [options.onChangeVertices, exceedsLimit]);
	// Bind refs to current Polygon and listeners
	const onLoad = useCallback(
		(polygon) => {
			polygonRef.current = polygon;
			if (exceedsLimit) return;
			listenersRef.current.push(
				polygon.addListener('mousedown', () => {
					const path = polygon.getPath();
					listenersRef.current.push(path.addListener('set_at', onEdit));
					listenersRef.current.push(path.addListener('insert_at', onEdit));
					listenersRef.current.push(path.addListener('remove_at', onEdit));
				})
			);
		},
		[onEdit, exceedsLimit]
	);
	const onUnmount = useCallback(() => {
		listenersRef.current.forEach((lis) => lis.remove());
		polygonRef.current = null;
	}, []);
	return (
		<PolygonGoogleMaps
			editable={isEditable}
			draggable={isEditable}
			onDragEnd={isEditable ? onEdit : undefined}
			onClick={handleClick}
			onLoad={onLoad}
			options={options}
			visible={isVisible}
			onUnmount={onUnmount}
			// ref={polygonRef}
		/>
	);
};
const Polygons = ({ polygonData, ...props }) => {
	if (polygonData) {
		const { polygons, onClick, defaultShow, visible, editable } = polygonData;
		const { onPolygonClick, hasCoverageBoundary } = { ...props };
		return Object.keys(polygons).map((key) => (
			<Polygon
				key={`polygon-${key}`}
				value={polygons[key]}
				defaultShow={defaultShow}
				onClick={onClick || onPolygonClick}
				globalVisible={visible}
				globalEditable={editable}
				hasCoverageBoundary={hasCoverageBoundary}
			/>
		));
	}
	return null;
};
Polygon.propTypes = {
	value: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		info: PropTypes.any,
		options: PropTypes.object,
	}).isRequired,
	onClick: PropTypes.func,
	globalVisible: PropTypes.instanceOf(Set).isRequired,
	globalEditable: PropTypes.instanceOf(Set).isRequired,
	defaultShow: PropTypes.bool,
	hasCoverageBoundary: PropTypes.bool,
};

Polygons.propTypes = {
	polygonData: PropTypes.shape({
		polygons: PropTypes.objectOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				info: PropTypes.any,
				options: PropTypes.object,
			})
		),
		onClick: PropTypes.func,
		defaultShow: PropTypes.bool,
		visible: PropTypes.instanceOf(Set),
		editable: PropTypes.instanceOf(Set),
	}),
	onPolygonClick: PropTypes.func,
	hasCoverageBoundary: PropTypes.bool,
};
export default React.memo(Polygons);
